import {createRouter, createWebHistory} from 'vue-router'
import ROUTES from '@/config/ROUTES.ts'
import {type LocalizedRoute, localizedRoutes} from '@/extensions/langRouter'
import i18n from '@/i18n'
import {useChairStore, usePageStore} from '@/stores'
import type {Pinia} from 'pinia'

async function buildRoutes(pinia: Pinia): Promise<LocalizedRoute[]> {
  const pageStore = usePageStore(pinia)
  let pagesFr: Record<string, Page> = {}
  let pagesEn: Record<string, Page> = {}

  try {
    let current_locale = i18n.global.locale.value
    // Load pages for both locales to get titles in both languages
    // Used when custom slugs are defined in the CMS
    i18n.global.locale.value = 'fr'
    pagesFr = await pageStore.all()

    i18n.global.locale.value = 'en'
    pagesEn = await pageStore.all()

    i18n.global.locale.value = current_locale
  } catch (error) {
    console.warn('Failed to load pages for dynamic routing, using default routes', error)
  }

  const getPath = (
    template: string,
    defaultPath: string,
    defaultEnPath?: string
  ): {path: string; paths?: {en: string}; pageTitleFr?: string; pageTitleEn?: string} => {
    const pageFr = pagesFr[template]
    const pageEn = pagesEn[template]

    if (!pageFr?.slug && !pageEn?.slug) {
      return defaultEnPath ? {path: defaultPath, paths: {en: defaultEnPath}} : {path: defaultPath}
    }

    const frSlug = pageFr?.slug?.fr || defaultPath.substring(1)
    const enSlug = pageEn?.slug?.en || pageFr?.slug?.en || defaultEnPath?.substring(1) || defaultPath.substring(1)

    return {
      path: `/${frSlug}`,
      paths: {en: `/${enSlug}`},
      pageTitleFr: pageFr?.slug ? pageFr.title : undefined,
      pageTitleEn: pageEn?.slug ? pageEn.title : undefined,
    }
  }

  const createRoute = (
    template: string,
    defaultPath: string,
    defaultEnPath: string,
    name: string,
    component: () => Promise<any>,
    defaultTitle: string,
    children?: any[]
  ) => {
    const {pageTitleFr, pageTitleEn, ...pathConfig} = getPath(template, defaultPath, defaultEnPath)
    const route: any = {
      ...pathConfig,
      name,
      component,
      meta: {
        title: defaultTitle,
        titleFr: pageTitleFr,
        titleEn: pageTitleEn,
      },
    }
    if (children) {
      route.children = children
    }
    return route
  }

  const routes: LocalizedRoute[] = [
    {
      path: '/',
      name: ROUTES.HOME,
      component: () => import('@/views/HomePage.vue'),
      meta: {
        title: 'page_titles.home',
      },
    },
    createRoute('about', '/a-propos', '/about', ROUTES.ABOUT, () => import('@/views/AboutPage.vue'), 'page_titles.about'),
    createRoute('careers', '/carrieres', '/careers', ROUTES.CAREERS, () => import('@/views/CareersPage.vue'), 'page_titles.careers'),
    (() => {
      const {pageTitleFr, pageTitleEn} = getPath('documents', '/ressources', '/resources')
      return createRoute('documents', '/ressources', '/resources', ROUTES.DOCUMENT.BASE, () => import('@/views/Document/DocumentIndex.vue'), 'page_titles.resources', [
        {
          path: '',
          name: ROUTES.DOCUMENT.INDEX,
          component: () => import('@/views/Document/DocumentIndex.vue'),
          meta: {
            title: 'page_titles.resources',
            titleFr: pageTitleFr,
            titleEn: pageTitleEn,
          },
        },
      ])
    })(),
    (() => {
      const {pageTitleFr, pageTitleEn, ...pathConfig} = getPath('events', '/evenements', '/events')
      return {
        ...pathConfig,
        name: ROUTES.EVENT.BASE,
        children: [
          {
            path: '',
            name: ROUTES.EVENT.INDEX,
            component: () => import('@/views/Event/EventIndex.vue'),
            meta: {
              title: 'page_titles.events',
              titleFr: pageTitleFr,
              titleEn: pageTitleEn,
            },
          },
          {
            path: ':slug',
            name: ROUTES.EVENT.SHOW,
            component: () => import('@/views/Event/EventShow.vue'),
            meta: {
              title: 'page_titles.events',
              titleFr: pageTitleFr,
              titleEn: pageTitleEn,
            },
          },
        ],
      }
    })(),
    createRoute('funding', '/financement', '/funding', ROUTES.FUNDING, () => import('@/views/FundingPage.vue'), 'page_titles.funding'),
    (() => {
      const {pageTitleFr, pageTitleEn, ...pathConfig} = getPath('news', '/nouvelles', '/news')
      return {
        ...pathConfig,
        name: ROUTES.NEWS.BASE,
        children: [
          {
            path: '',
            name: ROUTES.NEWS.INDEX,
            component: () => import('@/views/News/NewsIndex.vue'),
            meta: {
              title: 'page_titles.news',
              titleFr: pageTitleFr,
              titleEn: pageTitleEn,
            },
          },
          {
            path: ':slug',
            name: ROUTES.NEWS.SHOW,
            component: () => import('@/views/News/NewsShow.vue'),
            meta: {
              title: 'page_titles.news',
              titleFr: pageTitleFr,
              titleEn: pageTitleEn,
            },
          },
        ],
      }
    })(),
    (() => {
      const {pageTitleFr, pageTitleEn} = getPath('posts', '/publications', '/posts')
      return createRoute('posts', '/publications', '/posts', ROUTES.POST.BASE, () => import('@/views/Post/PostIndex.vue'), 'page_titles.posts', [
        {
          path: '',
          name: ROUTES.POST.INDEX,
          component: () => import('@/views/Post/PostIndex.vue'),
          meta: {
            title: 'page_titles.posts',
            titleFr: pageTitleFr,
            titleEn: pageTitleEn,
          },
        },
      ])
    })(),
    createRoute('policies', '/politique-de-confidentialite', '/privacy-policy', ROUTES.POLICIES, () => import('@/views/PoliciesPage.vue'), 'page_titles.policies'),
    createRoute('projects', '/projets', '/projects', ROUTES.PROJECTS, () => import('@/views/ProjectsPage.vue'), 'page_titles.projects'),
    {
      path: '/:slug',
      name: 'page.show',
      component: () => import('@/views/CustomPage.vue'),
      // Title is dynamic for custom pages. Set in the custom page vue component.
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'catch-all',
      component: () => import('@/views/NotFound.vue'),
      meta: {
        title: 'page_titles.not_found',
      },
    },
  ]

  return routes
}

export async function createAppRouter(pinia: Pinia) {
  const routes = await buildRoutes(pinia)

  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    linkActiveClass: '-active',
    linkExactActiveClass: '-exact-active',
    routes: localizedRoutes(routes, ['fr', 'en']),
    scrollBehavior(to) {
      if (to.hash) {
        return {el: to.hash, behavior: 'smooth'}
      }

      return new Promise((resolve) => setTimeout(() => resolve({top: 0}), 200))
    },
  })

  router.beforeEach(async (to, _, next) => {
    const targetLocale = to.meta.locale as 'fr' | 'en'
    i18n.global.locale.value = targetLocale

    if (targetLocale === 'fr') return next()

    try {
      const chairStore = useChairStore()
      const chair = await chairStore.get()

      if (chair.disable_english_site) {
        i18n.global.locale.value = 'fr'

        if (to.name) {
          Object.assign(to, {name: (to.name as string).replace(/:en$/, ':fr'), replace: true})
          return next(to)
        }

        return next({name: `${ROUTES.HOME}:fr`, replace: true})
      }
    } catch {}

    next()
  })

  return router
}
